import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAgent, checkAgentSharing, addAgentSharing } from '../lib/database';

// Define the Agent type locally as it's not exported from database.ts
interface Agent {
  id: string;
  name: string;
  sharable?: boolean;
}

const InvitePage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const { session } = useAuth();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!agentId || !session?.user) {
      setIsLoading(false);
      return;
    }

    const checkSharingAndLoadAgent = async () => {
      try {
        const [agentData, isShared] = await Promise.all([
          getAgent(agentId),
          checkAgentSharing(agentId, session.user.id)
        ]);

        if (!agentData) {
          setError('Invitation invalid: Agent not found.');
          setIsLoading(false);
          return;
        }
        
        // Cast agentData to Agent to satisfy TypeScript
        const typedAgentData = agentData as Agent;
        setAgent(typedAgentData);

        if (isShared) {
          navigate(`/agent/${agentId}`);
          return;
        }

        if (!typedAgentData.sharable) {
            setError('This agent is not available for sharing.');
            setIsLoading(false);
            return;
        }

      } catch (err) {
        console.error('Error processing invitation:', err);
        setError('An error occurred while processing the invitation.');
      } finally {
        setIsLoading(false);
      }
    };

    checkSharingAndLoadAgent();
  }, [agentId, session, navigate]);

  const handleAccept = async () => {
    if (!agentId || !session?.user || isAccepting) return;

    setIsAccepting(true);
    try {
      await addAgentSharing(agentId, session.user.id, session.user.email!);
      navigate(`/agent/${agentId}`);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError('Failed to accept the invitation. Please try again.');
      setIsAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-netlify-teal border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="w-12 h-12 bg-netlify-teal rounded-xl flex items-center justify-center shadow-lg">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21a6 6 0 00-9-5.197M4 7a4 4 0 118 0 4 4 0 01-8 0z"></path></svg>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          You're invited!
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl sm:rounded-xl sm:px-10 border border-gray-200">
          {error ? (
            <div className="text-center text-red-600">{error}</div>
          ) : agent ? (
            <div className="space-y-6 text-center">
              <p className="text-sm text-gray-600">
                You have been invited to collaborate on the agent:
              </p>
              <p className="text-lg font-semibold text-gray-900">{agent.name}</p>
              <button
                onClick={handleAccept}
                disabled={isAccepting}
                className="w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm bg-netlify-teal text-white text-sm font-medium hover:bg-netlify-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-netlify-teal disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAccepting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span>Accepting...</span>
                  </>
                ) : (
                  'Accept Invitation'
                )}
              </button>
            </div>
          ) : (
             <div className="text-center text-gray-600">No invitation details available.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitePage;
